import keyBy from 'lodash/keyBy';
import { createSelector } from '@reduxjs/toolkit';
import { ComponentDescription, ComponentInstance, View } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { selectCurrentDescriptor } from '../../editor/selectors';
import getComponentDescriptionsByName from './utils/getComponentDescriptionsByName';
import {
  selectCurrentViewId,
  selectDraggedComponentId,
  selectIsolatedComponentId,
  selectIsolationMode,
  selectSelectedComponentId,
} from '../manipulation/selectors';
import type { AppProjectEditorRootState } from '../reducer';
import getComponentInstancePathMappingByIds from '../components/utils/getComponentInstancePathMappingByIds';
import { getPropertyByPath } from '../utils/objects';
import getComponentDropPositions, { ComponentDropPositions } from '../components/utils/getComponentDropPositions';
import getMergedStyleClasses from './utils/getMergedStyleClasses';
import getComponentLogicTabId from '../logic/getComponentLogicTabId';
import getViewLogicTabId from '../logic/getViewLogicTabId';
import getComponentInstanceParentMappingByIds from '../components/utils/getComponentInstanceParentMappingByIds';

const empty: never[] = [];
Object.freeze(empty);

const emptyObj: Record<never, never> = {};

/**
 * Selects all the views from the current DOLAN as an array.
 */
export const selectDolanViews = (state: AppProjectEditorRootState) => {
  const dolan = selectCurrentDescriptor(state);
  return dolan?.views;
};

/**
 * Selects all views from the current DOLAN mapped by their (numeric) IDs.
 */
export const selectDolanViewsById = createSelector(selectDolanViews, (views): { [id: string]: View } | undefined =>
  views == null ? undefined : keyBy(views, 'id'),
);

/**
 * Select all component descriptions in the currently edited DOLAN.
 */
export const selectDolanComponentDescriptions = (state: AppProjectEditorRootState) => {
  const dolan = selectCurrentDescriptor(state);
  return dolan?.components ?? empty;
};

/**
 * Select all component descriptions by their identifiers in the currently edited DOLAN.
 */
export const selectDolanComponentDescriptionsByName = (state: AppProjectEditorRootState) => {
  const dolan = selectCurrentDescriptor(state);
  return getComponentDescriptionsByName(dolan);
};

/**
 * Select all merged style classes in the currently edited DOLAN.
 */
export const selectMergedStyleClasses = (state: AppProjectEditorRootState) =>
  getMergedStyleClasses(selectCurrentDescriptor(state));

/**
 * Select the index of the current view in the edited DOLAN.
 */
export const selectCurrentViewIndex = (state: AppProjectEditorRootState): number | undefined => {
  const viewId = selectCurrentViewId(state);
  const views = selectCurrentDescriptor(state)?.views;
  return views?.findIndex((view) => view.id === viewId);
};

/**
 * Select the current view structure from the currently edited DOLAN.
 */
export const selectCurrentView = (state: AppProjectEditorRootState): View | undefined => {
  const viewId = selectCurrentViewId(state);
  const views = selectDolanViewsById(state);
  return viewId == null || !views ? undefined : (views?.[viewId] ?? undefined);
};

/**
 * Selects a default view from the current DOLAN. It's the first non-global canvas
 * page found, or if not any, then the global canvas. Returns undefined if the DOLAN has no views.
 */
export const selectDefaultView = (state: AppProjectEditorRootState): View | undefined => {
  const views = selectDolanViews(state);
  return views?.find((pg) => pg.type !== 'global') ?? views?.[0];
};

/**
 * Selects the current view, if there is any, or if not then the default view from
 * the current DOLAN.
 */
export const selectCurrentOrDefaultView = (state: AppProjectEditorRootState) =>
  selectCurrentView(state) || selectDefaultView(state);

/**
 * Select the root component of the currently edited DOLAN view.
 */
export const selectCurrentViewRootComponent = (state: AppProjectEditorRootState): ComponentInstance | null =>
  selectCurrentView(state)?.layout.root_component ?? null;

/**
 * Select a mapping where keys are component instance IDs in the current view and values are paths to
 * the corresponding component instances.
 *
 * As the result is cached until the view or its component structure changes, this selector should be used as a basis for
 * finding component instances by ID from the current view.
 */
export const selectCurrentViewComponentInstancesPathsByIds = (state: AppProjectEditorRootState) => {
  const currentViewRootComponent = selectCurrentViewRootComponent(state);
  return getComponentInstancePathMappingByIds(currentViewRootComponent);
};

/**
 * Select the descriptor path to the currently selected component instance, within the current view, if any.
 */
export const selectSelectedComponentInstancePathInView = (
  state: AppProjectEditorRootState,
): readonly ('children' | number)[] | null => {
  const componentInstancePathsByIds = selectCurrentViewComponentInstancesPathsByIds(state);
  const selectedComponentId = selectSelectedComponentId(state);
  if (selectedComponentId == null || !componentInstancePathsByIds) return null;
  return componentInstancePathsByIds[selectedComponentId] ?? null;
};
/**
 * Select the descriptor path to the currently selected component instance, if any.
 */
export const selectSelectedComponentInstancePath = createSelector(
  selectSelectedComponentInstancePathInView,
  selectCurrentViewIndex,
  (selectedComponentPath, currentViewIndex): (string | number)[] | null => {
    if (!selectedComponentPath || currentViewIndex == null) return null;
    return ['views', currentViewIndex, 'layout', 'root_component', ...selectedComponentPath];
  },
);

/**
 * Select the currently selected component instance from the currently edited DOLAN view, if any.
 */
export const selectSelectedComponentInstance = (state: AppProjectEditorRootState): ComponentInstance | null => {
  const selectedInstancePath = selectSelectedComponentInstancePath(state);
  const dolan = selectCurrentDescriptor(state);
  return selectedInstancePath != null ? (getPropertyByPath(dolan, selectedInstancePath) as ComponentInstance) : null;
};

/**
 * Select the description of the component instance currently selected in the editor.
 * Returns null if no component is selected, and undefined if the component description is not found.
 */
export const selectSelectedComponentDescription = (
  state: AppProjectEditorRootState,
): ComponentDescription | null | undefined => {
  const selectedComponent = selectSelectedComponentInstance(state);
  const componentDescriptions = selectDolanComponentDescriptionsByName(state);
  return selectedComponent ? componentDescriptions[selectedComponent.name] : null;
};

/**
 * Select the descriptor path to the currently isolated component instance, within the current view, if any.
 */
export const selectIsolatedComponentInstancePathInView = (
  state: AppProjectEditorRootState,
): readonly ('children' | number)[] | null => {
  const componentInstancePathsByIds = selectCurrentViewComponentInstancesPathsByIds(state);
  const isolatedComponentId = selectIsolatedComponentId(state);
  if (isolatedComponentId == null || !componentInstancePathsByIds) return null;
  return componentInstancePathsByIds[isolatedComponentId] ?? null;
};

/**
 * Select the descriptor path to the currently isolated component instance, if any.
 */
export const selectIsolatedComponentInstancePath = createSelector(
  selectIsolatedComponentInstancePathInView,
  selectCurrentViewIndex,
  (isolatedComponentPath, currentViewIndex): (string | number)[] | null => {
    if (!isolatedComponentPath || currentViewIndex == null) return null;
    return ['views', currentViewIndex, 'layout', 'root_component', ...isolatedComponentPath];
  },
);

/**
 * Select the currently isolated component instance from the currently edited DOLAN view, if any.
 */
export const selectIsolatedComponentInstance = (state: AppProjectEditorRootState): ComponentInstance | null => {
  const selectedInstancePath = selectIsolatedComponentInstancePath(state);
  const dolan = selectCurrentDescriptor(state);
  return selectedInstancePath != null ? (getPropertyByPath(dolan, selectedInstancePath) as ComponentInstance) : null;
};

/**
 * Select the currently isolated component instance from the currently edited DOLAN view,
 * but only if in FULL isolation mode. Returns null if not isolated or in "light" isolation mode.
 */
export const selectFullIsolatedComponentInstance = (state: AppProjectEditorRootState): ComponentInstance | null => {
  const isolationMode = selectIsolationMode(state);
  return isolationMode === 'full' ? selectIsolatedComponentInstance(state) : null;
};

/**
 * Select the description of the component instance currently isolated in the editor.
 * Returns null if no component is isolated, and undefined if the component description is not found.
 */
export const selectIsolatedComponentDescription = (
  state: AppProjectEditorRootState,
): ComponentDescription | null | undefined => {
  const isolatedComponent = selectIsolatedComponentInstance(state);
  const componentDescriptions = selectDolanComponentDescriptionsByName(state);
  return isolatedComponent ? componentDescriptions[isolatedComponent.name] : null;
};

/**
 * Returns the current root component, which is either the root of the current view, or the root component of the isolated
 * composite component, if full isolation mode is active.
 */
export const selectCurrentRootComponent = (state: AppProjectEditorRootState): ComponentInstance | null => {
  const isolatedComponentInstance = selectIsolatedComponentInstance(state);
  const isolationMode = selectIsolationMode(state);
  return (isolationMode === 'full' && isolatedComponentInstance) || selectCurrentViewRootComponent(state);
};

/**
 * Selects valid positions for inserting the given component instance in the current view.
 */
export const selectComponentInsertPositions = (
  state: AppProjectEditorRootState,
  componentId: string | null,
): ComponentDropPositions => {
  const currentRootComponent = selectCurrentRootComponent(state);
  const isolationMode = selectIsolationMode(state);
  if (isolationMode === 'light' || !currentRootComponent) return emptyObj;
  const descriptions = selectDolanComponentDescriptionsByName(state);
  return getComponentDropPositions(currentRootComponent, componentId, descriptions);
};

/**
 * Selects valid drop positions for the current component drag.
 */
export const selectComponentDragDropPositions = (state: AppProjectEditorRootState): ComponentDropPositions => {
  const draggedComponentId = selectDraggedComponentId(state);
  return selectComponentInsertPositions(state, draggedComponentId);
};

/**
 * Selects valid positions for inserting a brand new component.
 */
export const selectNewComponentInsertPositions = (state: AppProjectEditorRootState): ComponentDropPositions =>
  selectComponentInsertPositions(state, null);

export const selectCurrentViewComponentHasLogic = (state: AppProjectEditorRootState, componentId: string): boolean => {
  const view = selectCurrentView(state);

  if (view == null) {
    return false;
  }

  const isolatedComponentId = selectIsolatedComponentId(state);
  const outerCanvasId = getComponentLogicTabId(view.id, componentId);
  const canvasId = isolatedComponentId === componentId ? `${outerCanvasId}:isolated` : outerCanvasId;

  return Object.values(view.flows || {}).some(
    (flow) => !['event', 'tab'].includes(flow.type) && 'z' in flow && flow.z === canvasId,
  );
};

export const selectCurrentViewRootHasLogic = (state: AppProjectEditorRootState): boolean => {
  const view = selectCurrentView(state);

  if (view == null) {
    return false;
  }

  const canvasId = getViewLogicTabId(view.id);

  return Object.values(view.flows || {}).some(
    (flow) => !['event', 'tab'].includes(flow.type) && 'z' in flow && flow.z === canvasId,
  );
};

export const selectSelectedComponentHasLogic = (state: AppProjectEditorRootState): boolean => {
  const selectedComponentId = selectSelectedComponentId(state);

  if (selectedComponentId == null) {
    return false;
  }

  return selectCurrentViewComponentHasLogic(state, selectedComponentId);
};

/**
 * Selects all the parent component instances of the currently selected component instance.
 * The direct parent is the first item in the array, and the root component is the last item.
 * If no component is selected, an empty array is returned.
 */
export const selectSelectedComponentParentInstances = (state: AppProjectEditorRootState): ComponentInstance[] => {
  const selectedComponentId = selectSelectedComponentId(state);
  if (!selectedComponentId) return [];

  const currentRootComponent = selectCurrentRootComponent(state);
  const result: ComponentInstance[] = [];
  const parentsById = getComponentInstanceParentMappingByIds(currentRootComponent);
  let parent = parentsById[selectedComponentId];
  while (parent != null) {
    result.push(parent);
    parent = parentsById[parent.id];
  }
  return result;
};

/**
 * Selects the direct parent component instance of the currently selected component instance,
 * or null if no such component exists at the moment.
 */
export const selectSelectedComponentParentInstance = (state: AppProjectEditorRootState): ComponentInstance | null =>
  selectSelectedComponentParentInstances(state)[0] ?? null;
